import t from "@/lang/lang";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuDataOwnerTypes } from "@/services/backend/htz/ahu/types/ahu-data-owner-types";
import { useHtzWorkOrderShowQuery } from "@/services/backend/htz/work-order/api/work-order-api";
import { useHtzReportShowQuery } from "@/services/backend/htz/report/api/report-api";
import { useHtzSummaryListQuery } from "@/services/backend/htz/summary/api/summary-api";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export interface AhuDataOwner {
  // label names the owning document, e.g. "Arbeitsschein 2024-001".
  label: string;
  // path leads to the owning document.
  path: string;
  // immutable is whether the owning document was written down. Its ahu
  // data must not be changed anymore then.
  immutable: boolean;
}

/**
 * useAhuDataOwner resolves the document owning the ahu data: a work order
 * or a report. The ahu data follows the state of its owner, which is
 * therefore also checked directly: it is the document a user writes down.
 *
 * `notFound` is set once loading finished without finding the owner. The
 * caller must not treat the ahu data as editable then.
 */
export function useAhuDataOwner(ahuData: AirHandlingUnitData) {
  const isWorkOrder = ahuData.ownerType === AhuDataOwnerTypes.WorkOrder;
  const isReport = ahuData.ownerType === AhuDataOwnerTypes.Report;
  const ownerId = ahuData.ownerId ?? "";

  const workOrder = useHtzWorkOrderShowQuery(
    { id: ownerId },
    { skip: !isWorkOrder || !ownerId },
  );
  const report = useHtzReportShowQuery(
    { id: ownerId },
    { skip: !isReport || !ownerId },
  );
  // A report is shown within the summary listing it, so the link needs
  // the summary and its item.
  const summaries = useHtzSummaryListQuery(
    { report: { active: true, values: [ownerId] } },
    { skip: !isReport || !ownerId },
  );

  const isLoading =
    workOrder.isLoading || report.isLoading || summaries.isLoading;
  const error = workOrder.error || report.error || summaries.error;

  let owner: AhuDataOwner | undefined;
  if (isWorkOrder && workOrder.data) {
    const wo = workOrder.data;
    owner = {
      label: `${t("Arbeitsschein")} ${wo.referenceNumber}`,
      path: `/processes/htz/${wo.processId}/work-orders/${wo.id}`,
      immutable: isImmutable(wo),
    };
  }
  if (isReport && report.data && summaries.data) {
    const summary = summaries.data.items.find((s) =>
      s.items.some((item) => item.reportId === ownerId),
    );
    const item = summary?.items.find((i) => i.reportId === ownerId);
    if (summary && item) {
      owner = {
        label: `${t("Anlagenbericht")} ${report.data.referenceNumber}`,
        path: `/processes/htz/${summary.processId}/summaries/${summary.id}/items/${item.id}/report`,
        immutable: isImmutable(report.data),
      };
    }
  }

  const notFound = !isLoading && !error && owner === undefined;

  return { owner, isLoading, error, notFound };
}
